
import { useEffect, useState, useRef, useCallback } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Skeleton } from "@/components/ui/skeleton";
import { ZoomOut, X, Sun, Moon, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ImageViewerProps {
  imageUrl: string;
  name?: string;
  description?: string;
  onClose?: () => void;
  initialLightMode?: boolean;
  contentType?: string;
}

const ImageViewer = ({ 
  imageUrl, 
  name, 
  description, 
  onClose, 
  initialLightMode = false,
  contentType
}: ImageViewerProps) => {
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [lightMode, setLightMode] = useState(initialLightMode);
  const [fullSize, setFullSize] = useState(true); // Default to full size
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isMuted, setIsMuted] = useState(false);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const isMobile = useIsMobile();
  
  const mediaRef = useRef<HTMLImageElement | HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [mediaDimensions, setMediaDimensions] = useState({ width: 0, height: 0 });
  const [containerPadding] = useState({ top: 20, right: 20, bottom: 20, left: 20 });

  // Determine media type based on content type or URL
  const mediaType = useCallback(() => {
    if (contentType) {
      if (contentType.startsWith('video/')) return 'video';
      if (contentType === 'image/gif') return 'gif';
      return 'image';
    }
    
    // Fallback to URL extension checking
    const url = imageUrl.toLowerCase();
    if (url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov')) return 'video';
    if (url.endsWith('.gif')) return 'gif';
    return 'image';
  }, [imageUrl, contentType]);
  
  const isVideo = mediaType() === 'video';
  const isGif = mediaType() === 'gif';

  // Reset position when toggling view mode
  useEffect(() => {
    if (mediaLoaded) {
      setPosition({ x: 0, y: 0 });
      lastPositionRef.current = { x: 0, y: 0 };
      sizeContainer();
    }
  }, [fullSize, mediaLoaded]);

  // Prevent browser pull-to-refresh when lightbox is open
  useEffect(() => {
    const preventDefault = (e: TouchEvent) => {
      // Only prevent default on the viewport ref to allow buttons to work
      if (e.target && viewportRef.current?.contains(e.target as Node)) {
        e.preventDefault();
      }
    };
    
    const touchStartPrevent = (e: TouchEvent) => {
      // Only prevent on the viewport to allow buttons to work
      if (e.target && viewportRef.current?.contains(e.target as Node)) {
        e.preventDefault();
      }
    };

    const wheelPrevent = (e: WheelEvent) => {
      // Prevent pull-to-refresh on wheel/trackpad gestures
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && e.deltaY < 0) {
        e.preventDefault();
      }
    };

    // Add the passive: false option to allow preventDefault to work
    document.addEventListener('touchmove', preventDefault, { passive: false });
    document.addEventListener('touchstart', touchStartPrevent, { passive: false });
    document.addEventListener('wheel', wheelPrevent, { passive: false });
    
    // Disable body scrolling when lightbox is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('touchmove', preventDefault);
      document.removeEventListener('touchstart', touchStartPrevent);
      document.removeEventListener('wheel', wheelPrevent);
      document.body.style.overflow = '';
    };
  }, []);

  const toggleLightMode = useCallback(() => {
    setLightMode(prevMode => !prevMode);
  }, []);
  
  const toggleImageSize = useCallback(() => {
    setFullSize(prevSize => !prevSize);
  }, []);
  
  const toggleMute = useCallback(() => {
    if (isVideo && mediaRef.current) {
      const videoEl = mediaRef.current as HTMLVideoElement;
      videoEl.muted = !videoEl.muted;
      setIsMuted(videoEl.muted);
    }
  }, [isVideo]);

  const handleMediaLoad = () => {
    setMediaLoaded(true);
    
    // Store natural media dimensions
    if (mediaRef.current) {
      let naturalWidth, naturalHeight;
      
      if (isVideo) {
        const videoEl = mediaRef.current as HTMLVideoElement;
        naturalWidth = videoEl.videoWidth;
        naturalHeight = videoEl.videoHeight;
      } else {
        const imgEl = mediaRef.current as HTMLImageElement;
        naturalWidth = imgEl.naturalWidth;
        naturalHeight = imgEl.naturalHeight;
      }
      
      setMediaDimensions({
        width: naturalWidth,
        height: naturalHeight
      });
      
      // Center the media in the viewport
      centerMediaInViewport(naturalWidth, naturalHeight);
      
      // Size container after media loads
      sizeContainer();
      
      // Check initial mute state for videos
      if (isVideo) {
        setIsMuted((mediaRef.current as HTMLVideoElement).muted);
      }
    }
  };

  // Center the media initially based on viewport size and media dimensions
  const centerMediaInViewport = useCallback((mediaWidth: number, mediaHeight: number) => {
    if (!viewportRef.current || !containerRef.current) return;
    
    const viewportWidth = viewportRef.current.clientWidth;
    const viewportHeight = viewportRef.current.clientHeight;
    
    // Calculate initial position to center the media
    // Only apply if media is bigger than viewport (in fullSize mode)
    if (fullSize && (mediaWidth > viewportWidth || mediaHeight > viewportHeight)) {
      const xPosition = (viewportWidth - mediaWidth) / 2;
      const yPosition = (viewportHeight - mediaHeight) / 2;
      
      setPosition({ x: xPosition, y: yPosition });
      lastPositionRef.current = { x: xPosition, y: yPosition };
      
      if (containerRef.current) {
        containerRef.current.style.transform = `translate(${xPosition}px, ${yPosition}px)`;
      }
    }
  }, [fullSize]);

  const handleMediaError = () => {
    console.error('Error loading media:', imageUrl);
  };

  // Handle close action
  const handleClose = useCallback(() => {
    if (onClose) onClose();
    else window.history.back();
  }, [onClose]);

  // Apply bounds to prevent excessive panning
  const applyPanningBounds = useCallback((value: number, axis: 'x' | 'y') => {
    if (!mediaRef.current || !viewportRef.current) return value;
    
    let mediaWidth, mediaHeight;
    
    if (isVideo) {
      const videoEl = mediaRef.current as HTMLVideoElement;
      mediaWidth = videoEl.offsetWidth;
      mediaHeight = videoEl.offsetHeight;
    } else {
      const imgEl = mediaRef.current as HTMLImageElement;
      mediaWidth = imgEl.width;
      mediaHeight = imgEl.height;
    }
    
    const viewportWidth = viewportRef.current.clientWidth;
    const viewportHeight = viewportRef.current.clientHeight;
    
    // Allow panning a bit beyond the edges for better UX
    const overPanFactor = 0.2; // How much extra panning to allow beyond edges
    
    if (axis === 'x') {
      const minX = viewportWidth - mediaWidth - (mediaWidth * overPanFactor);
      const maxX = mediaWidth * overPanFactor;
      return Math.max(minX, Math.min(maxX, value));
    } else {
      const minY = viewportHeight - mediaHeight - (mediaHeight * overPanFactor);
      const maxY = mediaHeight * overPanFactor;
      return Math.max(minY, Math.min(maxY, value));
    }
  }, [isVideo]);

  // Direct position update for better performance using RAF
  const updatePosition = useCallback((x: number, y: number) => {
    if (!fullSize || !containerRef.current) return;
    
    // Apply bounds to prevent excessive panning
    const boundedX = applyPanningBounds(x, 'x');
    const boundedY = applyPanningBounds(y, 'y');
    
    // Store the position in the ref for immediate access
    lastPositionRef.current = { x: boundedX, y: boundedY };
    
    // Apply transform directly for performance (no state update during active dragging)
    containerRef.current.style.transform = `translate(${boundedX}px, ${boundedY}px)`;
  }, [fullSize, applyPanningBounds]);

  // Update state position only after drag ends (for persistence)
  const finalizePosition = useCallback(() => {
    setPosition(lastPositionRef.current);
  }, []);

  // Mouse and touch event handlers with improved performance
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't initiate dragging if clicking on a button or control
    if (!fullSize || e.target instanceof HTMLButtonElement) return;
    
    e.preventDefault();
    setIsDragging(true);
    setStartPosition({
      x: e.clientX - lastPositionRef.current.x,
      y: e.clientY - lastPositionRef.current.y
    });
    
    // Ensure the event doesn't bubble to parent elements
    e.stopPropagation();
  }, [fullSize]);
  
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Don't initiate dragging if touching a button or control
    if (!fullSize || e.target instanceof HTMLButtonElement) return;
    
    e.preventDefault(); // Prevent all browser gestures
    setIsDragging(true);
    setStartPosition({
      x: e.touches[0].clientX - lastPositionRef.current.x,
      y: e.touches[0].clientY - lastPositionRef.current.y
    });
    
    // Ensure the event doesn't bubble to parent elements
    e.stopPropagation();
  }, [fullSize]);
  
  // Use optimized movement handlers for smoother panning
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !fullSize) return;
    
    const newX = e.clientX - startPosition.x;
    const newY = e.clientY - startPosition.y;
    
    // Use direct DOM manipulation for smoother performance
    updatePosition(newX, newY);
    
    // Prevent default to avoid any browser behavior
    e.preventDefault();
  }, [isDragging, fullSize, startPosition, updatePosition]);
  
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging || !fullSize) return;
    
    // Only prevent default if we're actually dragging
    e.preventDefault();
    
    const newX = e.touches[0].clientX - startPosition.x;
    const newY = e.touches[0].clientY - startPosition.y;
    
    // Use direct DOM manipulation for smoother performance
    updatePosition(newX, newY);
  }, [isDragging, fullSize, startPosition, updatePosition]);
  
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      finalizePosition();
    }
  }, [isDragging, finalizePosition]);
  
  // Calculate dimensions and resize container
  const sizeContainer = useCallback(() => {
    if (!mediaRef.current || !containerRef.current || !viewportRef.current) return;
    
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Natural dimensions of the media
    let mediaNaturalWidth, mediaNaturalHeight;
    
    if (isVideo) {
      const videoEl = mediaRef.current as HTMLVideoElement;
      mediaNaturalWidth = videoEl.videoWidth;
      mediaNaturalHeight = videoEl.videoHeight;
    } else {
      const imgEl = mediaRef.current as HTMLImageElement;
      mediaNaturalWidth = imgEl.naturalWidth;
      mediaNaturalHeight = imgEl.naturalHeight;
    }
    
    // For fit-to-screen, calculate max dimensions for the viewport
    // Account for padding and some space for controls
    const maxMediaWidth = windowWidth - containerPadding.left - containerPadding.right - 40;
    const maxMediaHeight = windowHeight - containerPadding.top - containerPadding.bottom - 100;
    
    if (fullSize) {
      // In full size mode, show the original media size and make it draggable
      containerRef.current.style.width = 'auto';
      containerRef.current.style.height = 'auto';
      
      if (mediaRef.current) {
        mediaRef.current.style.width = `${mediaNaturalWidth}px`;
        mediaRef.current.style.height = `${mediaNaturalHeight}px`;
        mediaRef.current.style.maxWidth = 'none';
        mediaRef.current.style.maxHeight = 'none';
        mediaRef.current.style.cursor = 'move';
      }
      
      // Setup the viewport for overflow and scrolling
      viewportRef.current.style.overflow = 'hidden';
      viewportRef.current.style.width = '100%';
      viewportRef.current.style.height = '100%';
      
      // Center the media if it's bigger than the viewport
      centerMediaInViewport(mediaNaturalWidth, mediaNaturalHeight);
      
      // Apply transform based on current position
      containerRef.current.style.transform = `translate(${position.x}px, ${position.y}px)`;
    } else {
      // For fit mode, constrain within the viewport
      let mediaWidth, mediaHeight;
      
      // Determine which dimension needs more scaling
      if ((mediaNaturalWidth / maxMediaWidth) > (mediaNaturalHeight / maxMediaHeight)) {
        // Width is the constraining factor
        mediaWidth = maxMediaWidth;
        mediaHeight = mediaNaturalHeight * (maxMediaWidth / mediaNaturalWidth);
      } else {
        // Height is the constraining factor
        mediaHeight = maxMediaHeight;
        mediaWidth = mediaNaturalWidth * (maxMediaHeight / mediaNaturalHeight);
      }
      
      containerRef.current.style.width = `${mediaWidth}px`;
      containerRef.current.style.height = `${mediaHeight}px`;
      
      if (mediaRef.current) {
        mediaRef.current.style.width = '100%';
        mediaRef.current.style.height = '100%';
        mediaRef.current.style.maxWidth = '100%';
        mediaRef.current.style.maxHeight = '100%';
        mediaRef.current.style.cursor = 'zoom-in';
      }
      
      // Reset viewport
      viewportRef.current.style.overflow = 'visible';
      containerRef.current.style.transform = 'none';
    }
  }, [containerPadding, fullSize, position, centerMediaInViewport, isVideo]);

  // Handle window resize events
  useEffect(() => {
    const handleResize = () => {
      if (mediaLoaded) sizeContainer();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Set up global mouse/touch move and up handlers
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [mediaLoaded, handleMouseMove, handleTouchMove, handleMouseUp, sizeContainer]);

  // Stop click event propagation to prevent closing when clicking controls
  const handleButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Render the appropriate media element based on content type
  const renderMedia = () => {
    if (isVideo) {
      return (
        <video 
          ref={mediaRef as React.RefObject<HTMLVideoElement>}
          src={imageUrl}
          controls={!fullSize}
          autoPlay
          loop
          muted={isMuted}
          className={`${!mediaLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          style={{ 
            userSelect: 'none',
            WebkitUserSelect: 'none',
            touchAction: fullSize ? 'none' : 'auto',
          }}
          onLoadedData={handleMediaLoad}
          onError={handleMediaError}
          playsInline
        />
      );
    } else {
      // Handle both regular images and GIFs
      return (
        <img 
          ref={mediaRef as React.RefObject<HTMLImageElement>}
          src={imageUrl}
          alt={name || "Media"}
          className={`${!mediaLoaded ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          style={{ 
            userSelect: 'none',
            WebkitUserSelect: 'none',
            touchAction: fullSize ? 'none' : 'auto',
          }}
          draggable={false}
          crossOrigin="anonymous"
          onLoad={handleMediaLoad}
          onError={handleMediaError}
        />
      );
    }
  };

  return (
    <div 
      className={`fixed inset-0 flex flex-col items-center justify-center ${lightMode ? 'bg-gray-100' : 'bg-black'} transition-colors duration-300 z-50`}
    >
      {/* Top control bar */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        {fullSize && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              toggleImageSize();
            }}
            className={`p-2 rounded-full ${lightMode ? 'bg-gray-800 hover:bg-gray-900' : 'bg-black/50 hover:bg-black/70'}`}
            aria-label="Fit to screen"
            title="Fit to screen"
            size="icon"
            variant="ghost"
            style={{ touchAction: 'auto' }}
          >
            <ZoomOut size={20} className="text-white" />
          </Button>
        )}
        
        {isVideo && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              toggleMute();
            }}
            className={`p-2 rounded-full ${lightMode ? 'bg-gray-800 hover:bg-gray-900' : 'bg-black/50 hover:bg-black/70'}`}
            aria-label={isMuted ? "Unmute" : "Mute"}
            title={isMuted ? "Unmute" : "Mute"}
            size="icon"
            variant="ghost"
            style={{ touchAction: 'auto' }}
          >
            {isMuted ? <VolumeX size={20} className="text-white" /> : <Volume2 size={20} className="text-white" />}
          </Button>
        )}
        
        <Button
          onClick={(e) => {
            e.stopPropagation();
            toggleLightMode();
          }}
          className={`p-2 rounded-full ${lightMode ? 'bg-gray-800 hover:bg-gray-900' : 'bg-black/50 hover:bg-black/70'}`}
          aria-label={`Switch to ${lightMode ? 'dark' : 'light'} mode`}
          title={`Switch to ${lightMode ? 'dark' : 'light'} mode`}
          size="icon"
          variant="ghost"
          style={{ touchAction: 'auto' }}
        >
          {lightMode ? <Moon size={20} className="text-white" /> : <Sun size={20} className="text-white" />}
        </Button>
        
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          className={`p-2 rounded-full ${lightMode ? 'bg-gray-800 hover:bg-gray-900' : 'bg-black/50 hover:bg-black/70'}`}
          aria-label="Close"
          title="Close"
          size="icon"
          variant="ghost"
          style={{ touchAction: 'auto' }}
        >
          <X size={20} className="text-white" />
        </Button>
      </div>
      
      <div 
        ref={viewportRef}
        className="w-full h-full flex items-center justify-center overflow-auto"
        onClick={(e) => {
          // Close when clicking background (outside media)
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        <div 
          ref={containerRef}
          className={`relative flex items-center justify-center ${!fullSize ? 'cursor-zoom-in' : ''}`}
          onClick={fullSize ? undefined : toggleImageSize}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {!mediaLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Skeleton className={`h-[80vh] w-[80vw] max-w-full max-h-full`} />
            </div>
          )}
          
          {renderMedia()}
        </div>
      </div>
      
      {mediaLoaded && name && (
        <div className={`fixed bottom-0 left-0 w-full p-4 ${lightMode ? 'bg-white/80 text-gray-900' : 'bg-black/50 text-white'}`}>
          <h3 className="font-medium">{name}</h3>
          {description && <p className="text-sm opacity-80">{description}</p>}
        </div>
      )}
    </div>
  );
};

export default ImageViewer;
